import React, { createContext, useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';

export const AuthContext = createContext();

const API_URL  = 'https://ghartakfresh.com/api';
const BASE_URL = 'https://ghartakfresh.com';

export const AuthProvider = ({ children }) => {
  const [user, setUser]               = useState(null);
  const [token, setToken]             = useState(null);
  const [loading, setLoading]         = useState(true);
  const [ordersBadge, setOrdersBadge] = useState(0);

  // ✅ logout ref — interceptor ke andar latest logout function use ho
  const logoutRef = useRef(null);

  const logout = async () => {
    localStorage.clear();
    setUser(null);
    setToken(null);
    setOrdersBadge(0);
  };

  // ref hamesha updated rahega
  logoutRef.current = logout;

  // ✅ Axios instance — ek baar banta hai
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API_URL,
      timeout: 15000,
      headers: { 'Content-Type': 'application/json' }
    });

    // ── Request interceptor: har request pe token lagao ──
    instance.interceptors.request.use((config) => {
      const t = localStorage.getItem('token');
      if (t) config.headers.Authorization = `Bearer ${t}`;
      return config;
    });

    // ── Response interceptor: token expire pe auto logout ──
    instance.interceptors.response.use(
      (response) => response,   // success — as it is
      (error) => {
        const status  = error?.response?.status;
        const expired = error?.response?.data?.expired;
        const message = error?.response?.data?.message || '';

        const isExpired =
          status === 401 &&
          (expired === true ||
           message === 'Token expired' ||
           message === 'jwt expired');

        if (isExpired) {
          console.warn('🔐 Token expired — logging out');
          logoutRef.current?.();                    // logout karo
          // Login page pe bhejo (React Router ke bahar)
          window.location.href = '/login';
        }

        return Promise.reject(error);
      }
    );

    console.log('✅ Axios instance created with baseURL:', API_URL);
    return instance;
  }, []); // ← ek baar banta hai

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const storedToken = localStorage.getItem('token');
      const storedUser  = localStorage.getItem('user');
      if (storedToken) setToken(storedToken);
      if (storedUser)  setUser(JSON.parse(storedUser));
    } catch (err) {
      console.log('Load user error:', err?.message);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const res   = await api.post('/auth/login', { email, password });
      const token = res.data?.token;
      const user  = res.data?.user;
      if (!token) return { success: false, message: 'Token missing in response' };
      localStorage.setItem('token', token);
      if (user) localStorage.setItem('user', JSON.stringify(user));
      setToken(token);
      setUser(user);
      return { success: true, user };
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Login failed';
      return { success: false, message: msg };
    }
  };

  const register = async (userData) => {
    try {
      const res   = await api.post('/auth/register', userData);
      const token = res.data?.token;
      const user  = res.data?.user;
      if (!token) return { success: false, message: 'Token missing in response' };
      localStorage.setItem('token', token);
      if (user) localStorage.setItem('user', JSON.stringify(user));
      setToken(token);
      setUser(user);
      return { success: true, user };
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Registration failed';
      return { success: false, message: msg };
    }
  };

  const updateProfile = async (payload) => {
    try {
      const res         = await api.put('/user/profile', payload);
      const updatedUser = res?.data?.user;
      if (updatedUser) {
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
      return { success: true, user: updatedUser };
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Update failed';
      return { success: false, message: msg };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        register,
        updateProfile,
        logout,
        loading,
        API_URL,
        BASE_URL,
        api,
        ordersBadge,
        setOrdersBadge
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};